import { DateTime } from "luxon";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, DatePicker, FormContainer } from "@/components";
import { useBreadCrumbContext } from "@/contexts/breadcrumb";
import { buildDailyReport } from "@/helpers/daily-report";
import { formatLongDatetime } from "@/helpers/dates";
import { useCurrentStore, useDailyClosure, useSalesReport } from "../../hooks";
import ClosureDetails from "./closure";
import Sales from "./sales";
import Summary from "./summary";
import Vat from "./vat";

const DailyReport = () => {
	const { t } = useTranslation();
	const [date, setDate] = useState(DateTime.now());
	const currentStore = useCurrentStore();

	const pageTitle = useMemo(() => t("dailyReport.pageTitle"), [t]);
	useBreadCrumbContext([{ label: pageTitle }]);

	const { data: sales } = useSalesReport({
		storeId: currentStore?.id,
		from: date.startOf("day").toISO(),
		to: date.endOf("day").toISO(),
	});

	const report = useMemo(() => buildDailyReport(sales?.data), [sales]);

	// Closed by the API shortly after midnight, Paris time: none for today yet.
	const { data: closure } = useDailyClosure(currentStore?.id, date.toISODate());

	return (
		<FormContainer
			button={
				<div className="flex justify-between w-full gap-2 items-center print:hidden">
					<DatePicker
						value={date.toJSDate()}
						onChange={(v) => setDate(DateTime.fromJSDate(v) as DateTime<true>)}
					/>
					<Button onClick={() => window.print()}>
						{t("dailyReport.print")}
					</Button>
				</div>
			}
			className="w-full max-w-4xl! gap-10! print:gap-5!"
			title={t("dailyReport.pageTitle")}
			isNotForm
		>
			<div className="flex justify-between items-end">
				<div className="flex flex-col gap-1">
					<h1>{currentStore?.name}</h1>
					<h3>
						{t("dailyReport.pageTitle")} — {date.toFormat("dd/MM/yyyy")}
					</h3>
				</div>
				<p className="text-small">
					{t("dailyReport.generatedAt", {
						date: formatLongDatetime(DateTime.now()),
					})}
				</p>
			</div>

			<Summary report={report} />
			<Vat report={report} />
			<Sales report={report} />
			{closure && <ClosureDetails closure={closure} />}
		</FormContainer>
	);
};

export default DailyReport;
